import { Component, HostListener, OnDestroy, OnInit, Type, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Media, Shop } from '@digilize/shared/definitions/src';
/* Straight at the component, not through the lib's barrel: that index pulls all ~120 components of
   `SharedUiComponentsModule` — CKEditor, the colour picker, the virtual-scroll tables — into the
   initial bundle for this one dialog. The component carries its own template dependencies. */
import { DialogConfirmComponent } from '@digilize/shared/ui/components/src/lib/dialog-confirm/dialog-confirm.component';
import { setPaulyCustomTheme } from '@digilize/shared/utils/helpers/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { TranslocoService } from '@jsverse/transloco';
import { AppContext } from '@plys/src/application/app.context';
import { FaviconService } from '@plys/src/application/services/favicon.service';
import { ShopSessionService } from '@plys/src/application/services/shop-session.service';
import { TagManagerService } from '@plys/src/application/services/tag-manager.service';
import { CartState } from '@plys/src/application/states/cart.state';
import { CheckoutState, PosContext } from '@plys/src/application/states/checkout.state';
import { LogoState } from '@plys/src/application/states/logo.state';
import { SeatsState } from '@plys/src/application/states/seats.state';
import { ShowState } from '@plys/src/application/states/show.state';
import { AuthState } from '@plys/src/auth/application/auth.state';
import { environment } from '@plys/src/environments/environment';
import { StateService } from '@plys/src/infrastructure/state-service.service';
import { SeatsSelectionStep } from '@plys/src/models/step';
import { CheckoutUIService } from '@plys/src/ui/checkout/checkout-u-i.service';
import { ThemeService } from '@plys/src/ui/theme/theme.service';
import { combineLatest, concatMap, filter, first, forkJoin, of, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'plys-checkout-wrapper',
  templateUrl: './checkout-wrapper.component.html',
  styleUrls: ['./checkout-wrapper.component.scss'],
  providers: [CheckoutUIService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CheckoutWrapperComponent implements OnInit, OnDestroy {
  private readonly SHOW_ID_PARAM_KEY = 'showId';
  private readonly POS_ID_PARAM_KEY = 'posId';
  readonly time$ = this.cartState.time$;
  readonly expiredError$ = this.cartState.expiredError$;
  /** The staging-only state debugger, once its chunk is in. Never set anywhere else. */
  stateDebugger: Type<unknown> = null;

  themeSet = false;
  /** Opens the checkout shell. Reached as soon as what the buyer's first step needs is in. */
  checkoutReady = false;
  readonly seatSelectionType$ = this.checkoutState.seatSelectionType$.pipe(
    filter((type) => type !== null),
    tap((type) => {
      this.checkoutState.setTicketsSteps(type);
    })
  );

  private readonly destroy = new Subject<boolean>();
  logo$ = this.logoState.logo$;
  private posFaviconLight: Media = null;
  private posFaviconDark: Media = null;
  private expireDialogOpened = false;
  private checkoutOpened = false;
  constructor(
    private showState: ShowState,
    private checkoutState: CheckoutState,
    private cartState: CartState,
    private seatsState: SeatsState,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private matSnackBar: MatSnackBar,
    private checkoutUIService: CheckoutUIService,
    private appContext: AppContext,
    private themeService: ThemeService,
    private transloco: TranslocoService,
    private logoState: LogoState,
    private dialogService: DialogService,
    public stateService: StateService,
    private shopSessionService: ShopSessionService,
    private faviconService: FaviconService,
    private authState: AuthState,
    private tagManagerService: TagManagerService
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  handleWindowClose(event: BeforeUnloadEvent): void {
    combineLatest([
      this.checkoutState.checkoutCancelConfirmation$,
      this.cartState.expiredError$,
      this.cartState.cartId$,
    ])
      .pipe(first())
      .subscribe(([requiresConfirmation, expiredError, cartId]) => {
        if (requiresConfirmation && expiredError === null && cartId) {
          event.preventDefault();
          event.returnValue = true;
        }
      });
  }

  ngOnInit(): void {
    this.stateService.lastPossibleStep$.pipe(takeUntil(this.destroy)).subscribe((lastPossibleStep) => {
      this.checkoutUIService.goToStep(lastPossibleStep.route, false);
    });
    this.checkoutState.onError$.pipe(takeUntil(this.destroy)).subscribe((beMessage) => {
      const message = beMessage || this.transloco.translate('errors.something_went_wrong');

      this.transloco.selectTranslate('global.close').subscribe((t) => {
        this.matSnackBar.open(message, t, {
          duration: 5000,
        });
      });
    });

    this.cartState.timedOut$.pipe(takeUntil(this.destroy)).subscribe((timeout) => {
      if (timeout) {
        this.cartState.setTimedOut(false);
        this.cartState.setExpiredError();
      }
    });

    this.cartState.expiredError$.pipe(takeUntil(this.destroy)).subscribe((expired) => {
      expired && this.openExpireDialog();
    });

    /* Was `*ngIf="environment.staging"` on the element itself, which still shipped the panel's 24kB
       to production. Fetched here instead, so only staging and acceptance ever download it. */
    if (environment.staging) {
      import(/* webpackChunkName: "state-debugger" */ '../../state-debugger/state-debugger.component').then(
        (m) => (this.stateDebugger = m.StateDebuggerComponent)
      );
    }

    this.init();
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

  private openExpireDialog(): void {
    // Both triggers can land at once — the countdown hitting zero and the request that was already in
    // flight coming back `cart_expired` — and two stacked unclosable dialogs cannot be dismissed.
    if (this.expireDialogOpened) {
      return;
    }
    this.expireDialogOpened = true;

    const dialog = this.dialogService.openDialog(DialogConfirmComponent, {
      disableClose: true,
      width: '670px',
      data: {
        mainHeader: 'errors.order_expired.title',
        subHeader: 'errors.order_expired.message',
        forcedAgree: true,
        confirmBtnText: 'global.continue',
      },
    });

    dialog.afterClosed().subscribe(() => {
      // Dropped whole, and only now: the reload follows in the same breath, so nothing renders or
      // reserves against the emptied cart in between — and nothing is left to restore a dead cart id.
      this.cartState.clearCartState();
      this.checkoutUIService.goToStep(this.checkoutState.firstStep?.route ?? 'tickets', true);
      setTimeout(() => {
        location.reload();
      }, 50); /* required for queryParams to reset/update */
    });
  }

  private init(): void {
    this.activatedRoute.paramMap
      .pipe(
        concatMap((params) => {
          const posId = params.get(this.POS_ID_PARAM_KEY);
          const showId = params.get(this.SHOW_ID_PARAM_KEY);
          if (!posId) {
            throw new Error('POS ID is missing');
          }

          if (!showId) {
            throw new Error('Show ID is missing');
          }
          this.appContext.setPosId(posId);
          this.appContext.setShowId(showId);

          const cartId = this.cartState.getCartIdValue() || this.activatedRoute.snapshot.queryParamMap.get('cart_id');

          // A cart is created against the show's area, so the context is published the moment the
          // show itself lands — the product list is now on screen well before the rest of the
          // bootstrap, and a buyer can add to the cart from it.
          this.showState.show$
            .pipe(
              filter((show) => !!show),
              first(),
              takeUntil(this.destroy)
            )
            .subscribe((show) => this.appContext.init(show.id, show.areaId));

          /* One round trip for everything the two route params can already ask for. The POS carries
             its shop, so the branding is no longer a read of its own, and the taken seats and the
             POS add-ons no longer queue behind the show. The seat catalogue is keyed by the show's
             shop and area, so it cannot join this wave — but it no longer holds anything up. */
          return forkJoin({
            pos: this.checkoutState.fetchPosById(posId).pipe(tap((pos) => this.applyPos(posId, pos))),
            show: this.showState
              .fetchShow(showId, posId)
              .pipe(tap((show) => show.disableAssignedSeating && this.checkoutState.skipSeatsSelection())),
            addons: this.showState.fetchAddons(posId),
            taken: this.seatsState.fetchTaken(showId),
          }).pipe(
            tap(({ addons, show }) => {
              // Only here, not in the add-ons branch itself: the flow those steps belong to is the
              // POS's to name, and in one round trip either read can land first.
              this.stateService.tryToSkipAddonsStep(addons);
              // Started, not waited on: nothing before the seat map is drawn needs the catalogue, so
              // it now runs alongside the cart restore instead of in front of it. Everyone who does
              // need it — the restore's seat lines, the seat map — joins this same read.
              this.seatsState
                .loadCatalogue(show.shopId, show.areaId)
                .pipe(takeUntil(this.destroy))
                .subscribe({ error: () => undefined });
              // A tickets-first checkout opens on the product list, which wants nothing from the
              // seat catalogue — so it is handed over here rather than a round trip later. A cart
              // still to be restored is not: which step it belongs on is only known afterwards.
              if (!cartId && !this.opensOnSeatMap()) {
                this.openCheckout(true);
              }
            }),
            // The one flow that cannot open without the catalogue is the one that opens on the map.
            concatMap(({ show }) =>
              this.opensOnSeatMap() ? this.seatsState.loadCatalogue(show.shopId, show.areaId) : of(null)
            ),
            tap(() => !cartId && this.openCheckout(true)),
            concatMap(() => this.stateService.fetchAndReinitFromActiveCartId(cartId))
          );
        }),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: (reinitDTO) => {
          // A restored cart has picked its own step by now; anything else starts at the beginning.
          this.openCheckout(!reinitDTO);
        },
        error: () => {
          this.router.navigate(['/show-not-found']);
        },
      });
  }

  /** Everything the POS read settles: the flow, the app context, the branding and the session. */
  private applyPos(posId: string, pos: PosContext): void {
    this.checkoutState.setTicketsSteps(pos.seatSelectionType);
    this.appContext.setShopId(pos.shopId);
    this.appContext.setPOSType(pos.posType);
    localStorage.setItem('plys_shop_id', pos.shopId);
    this.shopSessionService.ensureSession(pos.shopId);
    this.appContext.setProductIds(pos.productIds);
    this.appContext.setPosContext(posId, pos.theme, pos.seatsLimit, pos.orderTimeLimit, pos.disableSeatsValidation);
    this.posFaviconLight = pos.faviconLight;
    this.posFaviconDark = pos.faviconDark;
    this.tagManagerService.load(pos.tagManagerContainerId);
    this.themeService.setTheme(!pos.theme);
    // The shop rides along with the POS, so the shell is branded a full round trip earlier than it
    // was — before the show, the seat catalogue or the cart have landed.
    this.setShopTheming(pos.shop);
    // `/me` was answered at boot, when the shop was still unknown. Now that it is, a token left on
    // this origin by another shop can be dropped before anything is bought with it.
    this.authState.dropSessionOutsideShop(pos.shopId);
  }

  /** True when the flow opens on the seat map, which cannot be drawn before its catalogue is in. */
  private opensOnSeatMap(): boolean {
    return this.checkoutState.firstStep instanceof SeatsSelectionStep;
  }

  /**
   * Hands the checkout to the buyer, once — whichever stage of the bootstrap gets here first.
   * `restart` starts a flow that had nothing to restore on its first step.
   */
  private openCheckout(restart: boolean): void {
    if (this.checkoutOpened) {
      return;
    }
    this.checkoutOpened = true;

    if (!this.showState.loadedProducts?.length) {
      this.router.navigate(['/no-products']);
      return;
    }

    if (restart) {
      this.checkoutUIService.goToStep(this.checkoutState.firstStep?.route ?? 'tickets', true);
    }
    this.checkoutReady = true;
  }

  /**
   * Tolerates a POS served without its shop — an older backend — by branding with what the POS
   * itself carries rather than holding the whole checkout back for a read that no longer happens.
   */
  private setShopTheming(shop: Shop | null): void {
    this.logoState.shopWebsite = shop?.website;

    if (shop?.logo) {
      this.logoState.logo = shop.logo;
      localStorage.setItem('plys_shop_logo', JSON.stringify(shop.logo));
    }

    if (shop?.website) {
      localStorage.setItem('plys_shop_website', shop.website);
    }

    if (shop?.colors) {
      setPaulyCustomTheme(shop.colors);
      localStorage.setItem('plys_shop_colors', JSON.stringify(shop.colors));
    }

    /* per-slot fallback: use the POS favicon when set, otherwise fall back to the shop's favicon */
    this.faviconService.setFavicons({
      favicon_light: this.posFaviconLight ?? shop?.favicon_light,
      favicon_dark: this.posFaviconDark ?? shop?.favicon_dark,
    });

    this.themeSet = true;
  }
}
